import React, { useState } from 'react';
import { Calendar, Video, MessageSquare, Clock, Search, MoreVertical, CheckCircle2, XCircle, AlertTriangle, CreditCard, Play, CalendarClock, Link as LinkIcon, Plus } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { VideoCallModal } from '../ui/VideoCallModal';
interface ConsultationsTabProps {
  showToast: (msg: string) => void;
  setActiveTab?: (tab: string) => void;
}
type ConsultationStatus = 'upcoming' | 'completed' | 'cancelled';
type PaymentStatus = 'paid' | 'pending' | 'refunded';
type Format = 'video' | 'audio' | 'chat';
type Group = 'today' | 'tomorrow' | 'later';
interface Consultation {
  id: string;
  client: string;
  service: string;
  time: string;
  duration: string;
  format: Format;
  status: ConsultationStatus;
  payment: PaymentStatus;
  link: string;
  group: Group;
  timerMinutes?: number;
  warnings?: {
    type: 'unpaid' | 'unconfirmed' | 'no_link' | 'overlap';
    message: string;
  }[];
}
export function ConsultationsTab({
  showToast,
  setActiveTab
}: ConsultationsTabProps) {
  const [filter, setFilter] = useState<ConsultationStatus | 'all'>('upcoming');
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [activeClient, setActiveClient] = useState('');
  const [mobileMenuId, setMobileMenuId] = useState<string | null>(null);
  const [showRescheduleId, setShowRescheduleId] = useState<string | null>(null);
  const [consultations, setConsultations] = useState<Consultation[]>([{
    id: 'C-1042',
    client: 'Елена Смирнова',
    service: 'Натальная карта',
    time: '14:00',
    duration: '60 мин',
    format: 'video',
    status: 'upcoming',
    payment: 'paid',
    link: 'zoom.us/j/123456789',
    group: 'today',
    timerMinutes: 5
  }, {
    id: 'C-1043',
    client: 'Дмитрий Волков',
    service: 'Соляр на год',
    time: '16:30',
    duration: '45 мин',
    format: 'audio',
    status: 'upcoming',
    payment: 'pending',
    link: 'meet.google.com/abc',
    group: 'today',
    timerMinutes: 150,
    warnings: [{
      type: 'unpaid',
      message: 'Не оплачено'
    }]
  }, {
    id: 'C-1044',
    client: 'Анна Ковалева',
    service: 'Синастрия',
    time: '19:00',
    duration: '30 мин',
    format: 'chat',
    status: 'upcoming',
    payment: 'paid',
    link: '',
    group: 'today',
    timerMinutes: 300,
    warnings: [{
      type: 'unconfirmed',
      message: 'Клиент не подтвердил'
    }]
  }, {
    id: 'C-1045',
    client: 'Мария Петрова',
    service: 'Детский гороскоп',
    time: '10:00',
    duration: '60 мин',
    format: 'video',
    status: 'upcoming',
    payment: 'paid',
    link: '',
    group: 'tomorrow',
    warnings: [{
      type: 'no_link',
      message: 'Нет ссылки на сессию'
    }]
  }, {
    id: 'C-1046',
    client: 'Игорь Николаев',
    service: 'Бизнес-прогноз',
    time: '14:00',
    duration: '90 мин',
    format: 'video',
    status: 'upcoming',
    payment: 'paid',
    link: 'zoom.us/j/987654321',
    group: 'later'
  }, {
    id: 'C-1040',
    client: 'Ольга Сидорова',
    service: 'Экспресс-ответ',
    time: '09:00',
    duration: '15 мин',
    format: 'chat',
    status: 'completed',
    payment: 'paid',
    link: '',
    group: 'today'
  }]);
  // Handlers for consultation actions
  const handleCancelConsultation = (id: string) => {
    setConsultations(prev => prev.map(c => c.id === id ? {
      ...c,
      status: 'cancelled' as ConsultationStatus
    } : c));
    setMobileMenuId(null);
    showToast('Сессия отменена');
  };
  const handleReschedule = (id: string) => {
    setShowRescheduleId(id);
    showToast('Перенос сессии — выберите новое время в календаре');
    setMobileMenuId(null);
  };
  const filtered = consultations.filter(c => {
    if (filter === 'all') return true;
    return c.status === filter;
  });
  const groupedConsultations = {
    today: filtered.filter(c => c.group === 'today'),
    tomorrow: filtered.filter(c => c.group === 'tomorrow'),
    later: filtered.filter(c => c.group === 'later')
  };
  const handleStartSession = (client: string, format: Format) => {
    if (format === 'video' || format === 'audio') {
      setActiveClient(client);
      setShowVideoModal(true);
    } else {
      if (setActiveTab) setActiveTab('chat');else showToast('Открываю чат с клиентом');
    }
  };
  const getTimerColor = (minutes?: number) => {
    if (minutes === undefined) return '';
    if (minutes < 5) return 'text-rose-600 bg-rose-50 border-rose-200 animate-pulse';
    if (minutes < 15) return 'text-orange-600 bg-orange-50 border-orange-200';
    if (minutes < 60) return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-slate-600 bg-slate-50 border-slate-200';
  };
  const getFormatIcon = (format: Format) => {
    switch (format) {
      case 'video':
        return <Video className="w-4 h-4" data-id="element-12711" />;
      case 'audio':
        return <Clock className="w-4 h-4" data-id="element-12712" />;
      case 'chat':
        return <MessageSquare className="w-4 h-4" data-id="element-12713" />;
    }
  };
  const renderConsultationCard = (c: Consultation) => <div key={c.id} className="flex flex-col md:flex-row md:items-center justify-between p-3 sm:p-4 rounded-xl border border-slate-200 hover:border-indigo-300 transition-colors bg-white gap-4 group" data-id="element-12714">
      {/* Left: Info */}
      <div className="flex items-start gap-3 sm:gap-4 flex-1" data-id="element-12715">
        {/* Time & Format Badge */}
        <div className="flex flex-col items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-slate-50 border border-slate-100 flex-shrink-0" data-id="element-12716">
          <span className="text-sm sm:text-base font-bold text-slate-900 leading-none mb-1" data-id="element-12717">
            {c.time}
          </span>
          <div className={`flex items-center justify-center w-6 h-6 rounded-full ${c.format === 'video' ? 'bg-indigo-100 text-indigo-600' : c.format === 'audio' ? 'bg-amber-100 text-amber-600' : 'bg-purple-100 text-purple-600'}`} data-id="element-12718">
            {getFormatIcon(c.format)}
          </div>
        </div>

        <div className="flex-1 min-w-0" data-id="element-12719">
          <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-12720">
            <h4 className="text-sm sm:text-base font-bold text-slate-900 truncate" data-id="element-12721">
              {c.client}
            </h4>
            {c.payment === 'paid' ? <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-100" data-id="element-12722">
                <CheckCircle2 className="w-3 h-3 mr-1" data-id="element-12723" />
                Оплачено
              </span> : c.payment === 'pending' ? <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-50 text-rose-600 border border-rose-100" data-id="element-12724">
                <CreditCard className="w-3 h-3 mr-1" data-id="element-12725" />
                Не оплачено
              </span> : null}
          </div>

          <div className="text-xs sm:text-sm text-slate-600 mb-1.5 truncate" data-id="element-12726">
            {c.service}
            <span className="text-slate-300 mx-1" data-id="element-12727">•</span>
            {c.duration}
          </div>

          {/* Warnings & Timer Row */}
          <div className="flex flex-wrap items-center gap-2" data-id="element-12728">
            {c.status === 'upcoming' && c.timerMinutes !== undefined && <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold border ${getTimerColor(c.timerMinutes)}`} data-id="element-12729">
                <Clock className="w-3 h-3 mr-1" data-id="element-12730" />
                {c.timerMinutes < 60 ? `Через ${c.timerMinutes} мин` : `Через ${Math.floor(c.timerMinutes / 60)} ч`}
              </span>}
            {c.warnings?.map((w, i) => <span key={i} className="inline-flex items-center text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" title={w.message} data-id="element-12731">
                <AlertTriangle className="w-3 h-3 mr-1" data-id="element-12732" />
                {w.message}
              </span>)}
            {c.link && c.status === 'upcoming' && <button onClick={() => showToast('Ссылка скопирована')} className="inline-flex items-center text-[10px] font-medium text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-1.5 py-0.5 rounded transition-colors" data-id="element-12733">
                <LinkIcon className="w-3 h-3 mr-1" data-id="element-12734" />
                Ссылка
              </button>}
          </div>
        </div>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2 w-full md:w-auto mt-3 md:mt-0 pt-3 md:pt-0 border-t md:border-t-0 border-slate-100" data-id="element-12735">
        {c.status === 'upcoming' ? <>
            <Button variant="primary" size="sm" className={`flex-1 md:flex-none ${c.format === 'video' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-blue-600 hover:bg-blue-700'}`} icon={c.format === 'video' ? <Play className="w-4 h-4" data-id="element-12737" /> : <MessageSquare className="w-4 h-4" data-id="element-12738" />} onClick={() => handleStartSession(c.client, c.format)} data-id="element-12736">
              Начать
            </Button>
            <div className="hidden md:flex items-center gap-2" data-id="element-12739">
              <Button variant="outline" size="sm" onClick={() => {
            if (setActiveTab) setActiveTab('chat');else showToast('Открыт чат');
          }} data-id="element-12740">
                Написать
              </Button>
              <Button variant="ghost" size="sm" className="px-2" title="Перенести" onClick={() => handleReschedule(c.id)} data-id="element-12741">
                <CalendarClock className="w-4 h-4 text-slate-500" data-id="element-12742" />
              </Button>
              <Button variant="ghost" size="sm" className="px-2 hover:text-rose-600 hover:bg-rose-50" title="Отменить" onClick={() => handleCancelConsultation(c.id)} data-id="element-12743">
                <XCircle className="w-4 h-4" data-id="element-12744" />
              </Button>
            </div>
            {/* Mobile More Menu */}
            <div className="relative md:hidden" data-id="element-12745">
              <Button variant="outline" size="sm" className="px-2" onClick={() => setMobileMenuId(mobileMenuId === c.id ? null : c.id)} data-id="element-12746">
                <MoreVertical className="w-4 h-4 text-slate-500" data-id="element-12747" />
              </Button>
              {mobileMenuId === c.id && <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl shadow-lg border border-slate-100 py-1 z-30 animate-in fade-in slide-in-from-top-2" data-id="element-12748">
                  <button onClick={() => {
              if (setActiveTab) setActiveTab('chat');else showToast('Открыт чат');
              setMobileMenuId(null);
            }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-id="element-12749">
                    <MessageSquare className="w-4 h-4 text-slate-400" data-id="element-12750" />{' '}
                    Написать
                  </button>
                  <button onClick={() => handleReschedule(c.id)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-id="element-12751">
                    <CalendarClock className="w-4 h-4 text-slate-400" data-id="element-12752" />{' '}
                    Перенести
                  </button>
                  <button onClick={() => handleCancelConsultation(c.id)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 border-t border-slate-100" data-id="element-12753">
                    <XCircle className="w-4 h-4" data-id="element-12754" /> Отменить
                  </button>
                </div>}
            </div>
          </> : c.status === 'completed' ? <Badge variant="neutral" size="sm" className="w-full md:w-auto justify-center" data-id="element-12755">
            <CheckCircle2 className="w-3 h-3 mr-1" data-id="element-12756" />
            Завершена
          </Badge> : <Badge variant="error" size="sm" className="w-full md:w-auto justify-center" data-id="element-12757">
            <XCircle className="w-3 h-3 mr-1" data-id="element-12758" />
            Отменена
          </Badge>}
      </div>
    </div>;
  return <div className="space-y-4 sm:space-y-6 animate-in fade-in duration-300" data-id="element-12759">
      {/* Header & Primary Action */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-sm" data-id="element-12760">
        <div data-id="element-12761">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-12762">
            Расписание сессий
          </h2>
          <p className="text-slate-500 text-sm mt-1" data-id="element-12763">
            Управление предстоящими консультациями
          </p>
        </div>
        <Button variant="primary" size="md" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 shadow-sm" icon={<Plus className="w-4 h-4" data-id="element-12765" />} onClick={() => showToast('Открыта форма назначения сессии')} data-id="element-12764">
          Назначить сессию
        </Button>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-12766">
        <div className="flex bg-white border border-slate-200 p-1 rounded-xl overflow-x-auto w-full sm:w-auto shadow-sm" data-id="element-12767">
          <button onClick={() => setFilter('upcoming')} className={`px-4 py-1.5 text-sm font-bold rounded-lg whitespace-nowrap transition-all ${filter === 'upcoming' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-12768">
            Предстоящие
          </button>
          <button onClick={() => setFilter('completed')} className={`px-4 py-1.5 text-sm font-bold rounded-lg whitespace-nowrap transition-all ${filter === 'completed' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-12769">
            Прошедшие
          </button>
          <button onClick={() => setFilter('all')} className={`px-4 py-1.5 text-sm font-bold rounded-lg whitespace-nowrap transition-all ${filter === 'all' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-12770">
            Все
          </button>
        </div>

        <div className="relative w-full sm:w-72" data-id="element-12771">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-12772" />
          <input type="text" placeholder="Поиск по имени или ID..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 shadow-sm bg-white" data-id="element-12773" />
        </div>
      </div>

      {/* List Area */}
      <div className="space-y-6" data-id="element-12774">
        {filter === 'upcoming' || filter === 'all' ? <>
            {groupedConsultations.today.length > 0 && <div className="space-y-3" data-id="element-12775">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2 ml-1" data-id="element-12776">
                  <span className="w-2 h-2 rounded-full bg-indigo-500" data-id="element-12777" />
                  Сегодня
                </h3>
                <div className="space-y-2" data-id="element-12778">
                  {groupedConsultations.today.map(renderConsultationCard)}
                </div>
              </div>}

            {groupedConsultations.tomorrow.length > 0 && <div className="space-y-3" data-id="element-12779">
                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2 ml-1" data-id="element-12780">
                  <span className="w-2 h-2 rounded-full bg-slate-300" data-id="element-12781" />
                  Завтра
                </h3>
                <div className="space-y-2" data-id="element-12782">
                  {groupedConsultations.tomorrow.map(renderConsultationCard)}
                </div>
              </div>}

            {groupedConsultations.later.length > 0 && <div className="space-y-3" data-id="element-12783">
                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2 ml-1" data-id="element-12784">
                  <span className="w-2 h-2 rounded-full bg-slate-200" data-id="element-12785" />
                  Позже
                </h3>
                <div className="space-y-2" data-id="element-12786">
                  {groupedConsultations.later.map(renderConsultationCard)}
                </div>
              </div>}
          </> : <div className="space-y-2" data-id="element-12787">
            {filtered.map(renderConsultationCard)}
          </div>}

        {filtered.length === 0 && <div className="text-center py-16 bg-white border border-slate-200 rounded-2xl border-dashed" data-id="element-12788">
            <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-3" data-id="element-12789" />
            <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-12790">
              Нет сессий
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-12791">
              В этой категории пока пусто
            </p>
          </div>}
      </div>

      {showVideoModal && <VideoCallModal isOpen={showVideoModal} onClose={() => setShowVideoModal(false)} astrologerName="Вы" clientName={activeClient} mode="video" data-id="element-12792" />}
    </div>;
}